import React from 'react'
import { colors } from 'shared-ui'
import { css } from '@emotion/react'
import { ReactSVG } from 'react-svg'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import useUnit from '../../hooks/useUnit'

export default function WeatherFieldCard({
    field,
    field1,
    field2,
    handleSelect,
    value,
    label,
    unit,
    icon,
    secondValue,
    ...props }) {

    const isClickable = !!handleSelect

    const isAvailable = useMemo(() => {
        return typeof value === 'number' && isFinite(value)
    }, [value])

    const { formatMessage } = useIntl()
    const { systemIsMetric, isUnitInCurrentSystem, convertToUnits } = useUnit()

    const { convertedValue, convertedSecondValue, formattedUnit } = useMemo(() => {
        // check if unit can / needs to be converted
        if (systemIsMetric || !isUnitInCurrentSystem(unit) || !isAvailable) {
            return {
                convertedValue: value,
                convertedSecondValue: secondValue,
                formattedUnit: unit
            }
        } else {
            const converted1 = convertToUnits(value, unit, { useSecondaryUnit: false })
            let converted2
            if (secondValue) {
                converted2 = convertToUnits(value, unit, { useSecondaryUnit: false })
            }

            return {
                convertedValue: converted1?.primaryValue,
                convertedSecondValue: converted2?.primaryValue,
                formattedUnit: converted1?.primaryUnit.name,
            }
        }
    }, [value, secondValue, unit, systemIsMetric, isAvailable])

    return (
        <div css={[style.wrapper, css`
                pointer-events: ${(!isAvailable || !isClickable) && 'none'};
                opacity:  ${!isAvailable && 0.8};
                min-width: 12em;
            `]}
            onClick={isClickable ? () => handleSelect(field) : () => { }}
            {...props}
        >
            <div css={style.card(field === field1 || field === field2)}>

                <ReactSVG
                    css={css`
                        grid-area: icon;
                        svg {
                            width: 100%;
                            height: 100%;
                            padding: 4px;
                        }
                    `}
                    src={icon}
                />

                <div css={css`grid-area: label;`}>
                    {label}
                </div>

                <div css={css`grid-area: value; font-size: 1.3em;`}>
                    {isAvailable ?
                        <>
                            {convertedValue}
                            {convertedSecondValue && (' / ' + convertedSecondValue)}
                            {` ${formattedUnit}`}
                        </>
                        :
                        formatMessage({ id: 'unavailable' })}
                </div>

            </div>
            <div css={[style.highlight, css`
                position: absolute;
                width: 100%;
                height: 3px;
                bottom: 0;
                background: ${(field === field1 && colors.dataPrimary)};
                background: ${(field === field2 && colors.dataSecondary)};
            `]} />
        </div>
    )
}

const style = {
    wrapper: css`
        flex-grow: 1; 
        background: ${colors.eventLight}; 
        border-radius: 5px; 
        display: grid; 
        place-items: center;
        position: relative;
        overflow: hidden;
        cursor: pointer;
        padding: 0.6em;
        user-select: none;
        -webkit-user-select: none;
        
        &:hover {
            > * {color: ${colors.main1}};
        }
    `,
    card: (isSelected) => css`
        display: grid;
        grid-template: 1.5em 1.5em / 3em 1fr;
        grid-template-areas: 
            'icon label'
            'icon value'; 
        grid-gap: 0.2em;
        align-items: center;
        color: ${isSelected ? colors.white : colors.soft};
    `,
    highlight: css`
        position: absolute;
        width: 100%;
        height: 3px;
        bottom: 0;
    `
}